import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useLoginApi } from "../api/login.js";
import { GlobalContext } from "../util/globalContext";

import "../styles/colors.css";
import "../styles/fonts.css";
import "../styles/common.css";
import "../styles/layout.css";

import { ReactComponent as BalerLogo } from "../assets/icons/baler.svg";
import AkiraLogo from "../assets/icons/akira_no_bg_white_border.png";

import CustomTextField from "../components/CustomTextField.jsx";
import CustomButton from "../components/CustomButton.jsx";
import { validatePhoneNumber, validatePassword } from "../util/validation.js";

export default function LoginPage({ onLoginSuccess }) {
  const { encryptPassword, loginCustomer } = useLoginApi();
  const { config } = useContext(GlobalContext);

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgLogo, setOrgLogo] = useState(null);    // base64 logo from API
  const [primaryColor, setPrimaryColor] = useState(""); // org brand color

  /* =====================================
     FETCH ORG NAME & LOGO BY SUBDOMAIN
  ===================================== */
  useEffect(() => {
    const fetchOrgBySubdomain = async () => {
      try {
        const hostname = window.location.hostname; // e.g. "akira.bcuai.in"
        const parts = hostname.split(".");
        const subdomain = parts.length >= 2 ? parts[0] : null;

        if (!subdomain || subdomain === "localhost" || subdomain === "www") {
          return;
        }

        const BASE_URL =
          config?.REACT_APP_BASE_URL || "http://localhost:5003/baler/api/v1";
        const response = await fetch(
          `${BASE_URL}/get_org_by_subdomain/${subdomain}`
        );
        const data = await response.json();

        if (response.ok && data?.data) {
          const orgData = data.data;

          // ✅ Org display name
          const displayName =
            orgData.name ||
            orgData.org_name ||
            subdomain.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase();
          setOrgName(displayName.toUpperCase());

          // ✅ Brand color — set on body as inline style so it overrides theme class
          if (orgData.primary_color) {
            setPrimaryColor(orgData.primary_color);
            document.body.style.setProperty(
              "--primary-color",
              orgData.primary_color
            );
          }

          // ✅ Logo from API
          if (orgData.logo) {
            setOrgLogo(orgData.logo);
          }
        }
      } catch (err) {
        console.error("Failed to fetch org by subdomain:", err);
      }
    };

    if (config) {
      fetchOrgBySubdomain();
    }
  }, [config]);

  /* =====================================
     APPLY THEME BASED ON ROLE
  ===================================== */
  function applyTheme(role) {
    document.body.classList.remove("admin-theme", "manufacturer-theme");

    if (role === "admin") {
      document.body.classList.add("admin-theme");
    } else {
      document.body.classList.add("manufacturer-theme");
    }
  }

  /* =====================================
     LOGIN SUBMIT
  ===================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePhoneNumber(mobile)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    if (!validatePassword(password)) {
      toast.error(
        "Password must be at least 6 characters and contain at least one letter and one number"
      );
      return;
    }

    try {
      const encryptedPassword = await encryptPassword(password);

      const result = await loginCustomer({
        phoneNo: mobile,
        encryptedPassword,
      });

      if (result.success) {
        toast.success("Login successful!");

        // ✅ SAVE TOKEN
        localStorage.setItem("token", result.token);

        // ✅ SAVE ROLE
        const role = result.user?.role || "manufacturer";
        localStorage.setItem("userRole", role);

        // ✅ APPLY THEME
        applyTheme(role);

        if (onLoginSuccess) onLoginSuccess();
      } else {
        toast.error(result.message || "Login failed");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    }
  };

  /* =====================================
     LOGO — prefer API logo, else fallback by role
  ===================================== */
  const savedRole = localStorage.getItem("userRole");
  const isAdmin = savedRole === "admin";

  const renderLogo = () => {
    if (orgLogo) {
      return <img src={orgLogo} alt={orgName} className="logo-svg" />;
    }
    if (isAdmin) {
      return <img src={AkiraLogo} alt="Logo" className="logo-svg" />;
    }
    return <BalerLogo className="logo-svg" />;
  };

  /* =====================================
     UI
  ===================================== */
  return (
    <div className="auth-wrap">
      <div className="auth-card">

        {/* LEFT PANEL */}
        <div
          className="auth-left"
          style={primaryColor ? { backgroundColor: primaryColor } : {}}
        >
          {renderLogo()}

          <div className="brand-title">
            {orgName || "..."}
          </div>

          <div className="brand-sub">
            {isAdmin ? "ADMIN CONTROL PANEL" : "INVENTORY MANAGEMENT"}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="auth-right">
          <form onSubmit={handleSubmit} className="login-form">

            <CustomTextField
              label="Mobile Number"
              value={mobile}
              numericOnly
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter 10-digit mobile number"
              required
            />

            <CustomTextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />

            <CustomButton type="submit" className="btn-sign">
              Sign In
            </CustomButton>
            <div className="forgot-link">
              <span onClick={() => window.location.href = "/forgot-password"}>
                Forgot Password?
              </span>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}